/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import cx from 'classnames';

import { SecurityHelper, useBreakpoint, toTitleCase } from 'sarsaparilla';

import { NavbarRight } from '../NavbarRight';
import { WidgetLogo } from './WidgetLogo';
import { fetchAssetById, fetchAssetByParkPassFacilityId } from './WidgetNavbar.utils';

import styles from './WidgetNavbar.module.scss';

interface WidgetNavbarProps {
    isGuestLoginAllowed?: boolean;
}

export function WidgetNavbar({ isGuestLoginAllowed }: WidgetNavbarProps) {
    const { isMdAndUp } = useBreakpoint();

    // State and refs
    const [widgetLocationName, setWidgetLocationName] = React.useState<string | null>(
        null
    );

    const loadWidgetLocation = React.useCallback(async () => {
        const id = SecurityHelper.getWidgetLocation();

        if (id) {
            try {
                let resp = await fetchAssetById(id);

                if (resp.data.results?.length > 0) {
                    setWidgetLocationName(resp.data.results[0].name);
                } else {
                    // fall back to lookup by pass facility id
                    resp = await fetchAssetByParkPassFacilityId(id);
                    if (resp.data.results?.length > 0) {
                        setWidgetLocationName(resp.data.results[0].name);
                    }
                }
            } catch (error) {
                // ignore
            }
        }
    }, []);

    React.useEffect(() => {
        SecurityHelper.storeWidgetReferrer();
        SecurityHelper.storeWidgetDocumentReferrer();
        loadWidgetLocation();
    }, [loadWidgetLocation]);

    const showLocationName = widgetLocationName && isMdAndUp;

    return (
        <>
            <div className={styles.widgetNavbarLeft}>
                {showLocationName ? (
                    // At larger sizes and when there's a location name, show the location name
                    <div className={styles.widgetLocationTitle}>
                        <a
                            href={SecurityHelper.getWidgetHomeUrl() ?? '#'}
                            className={cx(
                                styles.locationLink,
                                'heading-sm color-text-01'
                            )}
                        >
                            {toTitleCase(widgetLocationName)}
                        </a>
                    </div>
                ) : (
                    // Otherwise just show the logo
                    <WidgetLogo />
                )}
            </div>

            <div className={styles.widgetNavbarRight}>
                {showLocationName ? <WidgetLogo isCompact={true} /> : null}

                <NavbarRight
                    hideSearchBar={true}
                    isGuestLoginAllowed={isGuestLoginAllowed}
                />
            </div>
        </>
    );
}
